<script lang="ts" setup>
import { Field, FieldContent, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@forgd/ui-kit/input-group'
import { PaperAirplaneIcon } from '@heroicons/vue/24/outline'
import { useMutation } from '@pinia/colada'
import { z } from 'zod'

const toast = useAppToast()
const inviteEmailsId = useId()
const emailsSchema = z.array(z.string().email())
const inviteEmailsInput = ref('')

const { mutateAsync: inviteClientsToForgd, isLoading: isInvitePending } = useMutation(buildApiMutation('/market-makers/clients/invite', {
  method: 'post',
}))

async function handleInviteClientsClick() {
  if (inviteEmailsInput.value.trim().length === 0) {
    return
  }

  const emails = inviteEmailsInput.value
    .split(',')
    .map(email => email.trim())
    .filter(Boolean)
  const emailsValidation = emailsSchema.safeParse(emails)

  if (!emailsValidation.success) {
    toast.error('Some emails are invalid. Please check your input and try again.')
    return
  }

  const validatedEmails = emailsValidation.data

  if (validatedEmails.length > 10) {
    toast.warning('You can only invite up to 10 clients at a time.')
    return
  }

  try {
    await inviteClientsToForgd({
      body: {
        emails: validatedEmails,
      },
    })
    toast.success('Invitations sent successfully.')
  }
  catch {
    toast.error('Failed to invite clients. Please try again.')
  }
}
</script>

<template>
  <p class="text-accent text-base font-semibold mb-4">
    Invite Your Clients to Forgd
  </p>
  <p class="text-primal leading-tight text-4xl font-normal mb-8">
    Streamline your operations & improve your Leaderboard rank by providing data to Forgd.
  </p>
  <Field>
    <FieldLabel :for="inviteEmailsId">
      Enter the emails you want to invite, separated by commas (up to 10)
    </FieldLabel>
    <FieldContent>
      <InputGroup size="lg">
        <InputGroupInput
          :id="inviteEmailsId"
          v-model="inviteEmailsInput"
          placeholder="e.g. client@email.com, otheremail@ofuser.com"
        />
        <InputGroupAddon>
          <InputGroupButton
            variant="default"
            :icon-only="false"
            :loading="isInvitePending"
            @click="handleInviteClientsClick"
          >
            <PaperAirplaneIcon v-if="!isInvitePending" aria-hidden="true" />
            Send Invitations
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </FieldContent>
  </Field>
</template>
